import React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  GiftOutlined,
} from "@ant-design/icons";
import { Alert, Badge, Button, Card, Divider, Space, Tooltip, Typography } from "antd";
import { promocodeFormFields } from "@/Assets/Mocks/CommonPricing.mock";
import CommonForm from "@/Components/Common/CommonForm";

const { Text, Title } = Typography;

const Discount = ({ props }) => {
  const {
    plan,
    formRef,
    formValues,
    upgradePlan,
    cancelPlan,
    submitPromocode,
    trialDays,
    profileData,
    urlPromoCode,
    minuteTrialText,
  } = props;
  const [promoFormValue, setPromoFormValue] = useState(formValues);
  const { t } = useTranslation();
  promocodeFormFields[0].placeholder = t(`pricing.Enter Promocode`);

  let trialText =
    profileData.trial_days === undefined || trialDays === 7
      ? t(`pricing.7 days free trial, No Credit card required`)
      : trialDays > 0 && trialDays < 7
      ? `${t("pricing.Your remaining trial days are")} ${trialDays}. ${t("pricing.You have used")} ${
          7 - trialDays
        } ${t("pricing.days.")}`
      : t(`pricing.No trial days remaining. You have already used the 7-day free trial.`);

  return (
    <React.Fragment>
      {plan.discountLabel && (
        <Space direction="horizontal" style={{ width: "100%", justifyContent: "space-between" }}>
          <Text type="secondary" style={{ textDecoration: "line-through" }}>
            ${plan.monthlyPrice ? plan.monthlyPrice : plan.price}
          </Text>
          <Space size="small" align="center">
            <GiftOutlined style={{ color: "#52c41a" }} />
            {plan.discountValue ? (
              <Text style={{ color: "#52c41a" }}>
                {plan.discountLabel} {t(`pricing.Discount applied`)}
              </Text>
            ) : (
              <Text style={{ color: "#52c41a" }}>
                {Math.round(100 - (plan.finalPrice * 100) / (plan.monthlyPrice || plan.price)) + "%"}{" "}
                {t(`pricing.Discount applied`)}
              </Text>
            )}
          </Space>
        </Space>
      )}
      {!plan.isPromoInputHidden && !plan.selected && (
        <Space.Compact style={{ width: "100%" }}>
          <CommonForm
            onSubmit={submitPromocode}
            initialValues={promoFormValue}
            formFields={promocodeFormFields}
            formRef={formRef}
            isSave={false}
            onFormChange={(e) => setPromoFormValue(e)}
          />
          <Button type="primary" size="small" onClick={() => submitPromocode(plan)}>
            {t(`pricing.Apply`)}
          </Button>
        </Space.Compact>
      )}
      {(plan.touched || urlPromoCode) && plan.discountValue && (
        <Text style={{ color: "#52c41a" }}>
          {t(`pricing.Your code(s)`)} <strong>{plan["discountValue"]}</strong>{" "}
          {t(`pricing.and discount have been applied successfully`)}
        </Text>
      )}
      {plan.touched && !plan.discountValue && !promoFormValue?.promocode && (
        <Space size="small" align="center">
          <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
          <Text type="danger">Your entered promocode is not correct, Enter a valid code</Text>
        </Space>
      )}

      {plan.selected && plan.type === "recurring" && (
        <Space size="small" align="center">
          <ClockCircleOutlined />
          <Text>
            {minuteTrialText} {t(`pricing.trial left`)}
          </Text>
        </Space>
      )}
      {plan.type === "recurring" &&
        (plan?.disableActiveButton ? (
          <Tooltip title={t("pricing.disableButtonTooltip")}>
            <Button type="primary" block disabled={true}>
              {t("pricing.Upgrade your plan now")}
            </Button>
          </Tooltip>
        ) : (
          <Button type="primary" block onClick={() => (plan.selected ? cancelPlan(plan) : upgradePlan(plan))}>
            {plan.trial && !plan.selected
              ? trialDays === 0 && profileData.trial_days !== undefined
                ? t("pricing.Upgrade your plan now")
                : `${t("pricing.Start")} ${profileData.trial_days === undefined ? plan.trial.days : trialDays} ${t(
                    "pricing.Day Free Trial"
                  )}`
              : t(`pricing.Cancel Plan`)}
          </Button>
        ))}
      {plan.type === "onetime" && !plan.selected && (
        <Button type="primary" block onClick={() => upgradePlan(plan)}>
          {t(`pricing.Upgrade`)}
        </Button>
      )}
      {plan.trial && plan.intervalLable !== "Unlimited" && !plan.selected && (
        <Text style={{ textAlign: "center", fontWeight: 500 }}>{trialText}</Text>
      )}
    </React.Fragment>
  );
};

export default function SinglePlan(props) {
  const { t } = useTranslation();
  const { plan } = props;
  return (
    <div className="pricing-card">
      <Card
        style={{
          padding: "24px",
          backgroundColor: plan.selected ? "#f6ffed" : "#ffffff",
          border: plan.selected ? "2px solid #52c41a" : "1px solid #d9d9d9",
          borderRadius: "8px",
        }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Title level={2} style={{ margin: 0 }}>
              {plan.name}
            </Title>
            {plan.selected && <Badge color="blue" text={t(`pricing.Current`)} />}
          </div>
          <Space size="small" align="baseline">
            <Title level={1} style={{ margin: 0, fontSize: "48px", fontWeight: "bold" }}>
              ${plan.finalPrice}
            </Title>
            {plan.intervalLable && (
              <Text type="secondary" style={{ fontSize: "16px", fontWeight: 500 }}>
                /{t(`pricing.${plan.intervalLable}`)}
              </Text>
            )}
          </Space>
          {plan.price !== 0 ? <Discount props={props} /> : <div style={{ minHeight: "100px" }}></div>}
          <Divider />
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {plan.features.map((feature, index) => (
              <Space key={index} size="small" align="start" style={{ width: "100%" }}>
                <CheckCircleFilled
                  style={{ color: "#52c41a", fontSize: "18px", marginTop: "2px", flexShrink: 0 }}
                />
                <Text style={{ flex: 1 }}>{t(`pricing.features.${feature.text}`)}</Text>
              </Space>
            ))}
          </Space>
        </Space>
      </Card>
    </div>
  );
}
